import { ReactNode } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { X } from 'lucide-react';

interface BaseFormModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback to close the modal */
  onClose: () => void;
  /** Modal title */
  title: string;
  /** Form content */
  children: ReactNode;
  /** Submit button text */
  submitText?: string;
  /** Cancel button text */
  cancelText?: string;
  /** Whether the form is submitting */
  isSubmitting?: boolean;
  /** Whether the submit button should be disabled */
  submitDisabled?: boolean;
  /** Form submission handler */
  onSubmit?: () => void;
  /** Form cancel handler (defaults to onClose) */
  onCancel?: () => void;
  /** Whether to show the cancel button */
  showCancel?: boolean;
  /** Whether to show the submit button */
  showSubmit?: boolean;
  /** Custom footer content */
  footer?: ReactNode;
  /** Modal size */
  size?: 'sm' | 'md' | 'lg' | 'xl';
}

/**
 * Base modal component for forms with consistent styling and behavior
 * Provides common modal patterns: header, footer, loading states
 */
export const BaseFormModal = ({
  isOpen,
  onClose,
  title,
  children,
  submitText = 'Save',
  cancelText = 'Cancel',
  isSubmitting = false,
  submitDisabled = false,
  onSubmit,
  onCancel,
  showCancel = true,
  showSubmit = true,
  footer,
  size = 'md',
}: BaseFormModalProps) => {
  const sizeClasses = {
    sm: 'max-w-md',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className={`${sizeClasses[size]} max-h-[90vh] overflow-y-auto`}>
        <DialogHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <DialogTitle className="text-xl font-semibold">{title}</DialogTitle>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="h-6 w-6 p-0"
          >
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </Button>
        </DialogHeader>

        <div className="space-y-6">
          {children}
        </div>

        {footer || (showCancel || showSubmit) ? (
          <div className="flex justify-end space-x-2 pt-6 border-t">
            {footer ? (
              footer
            ) : (
              <>
                {showCancel && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleCancel}
                    disabled={isSubmitting}
                  >
                    {cancelText}
                  </Button>
                )}
                {showSubmit && (
                  <Button
                    type="submit"
                    onClick={onSubmit}
                    disabled={submitDisabled || isSubmitting}
                    className="min-w-[100px]"
                  >
                    {isSubmitting ? 'Saving...' : submitText}
                  </Button>
                )}
              </>
            )}
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

export default BaseFormModal;